<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <h1 class="fw-bold page-title">
            센터플로우 구독 관리
          </h1>
          <span class="pageDesc">센터플로우 구독을 관리하고 조회할 수 있습니다.</span>
        </div>
      </div>
    </div>
    
    <!-- 검색 및 필터 영역 -->
    <div class="card-body px-0 pt-0 pb-2 table-with-search" style="margin-top: 20px">
      <div class="card content-wrap-card col-sm-12 col-xs-12 tableCard p-0">
        <div class="table-filter-container">
          <div class="filter-row">
            <div class="filter-item">
              <label>병원명</label>
              <a-input v-model:value="searchParams.hospitalName" placeholder="병원명을 입력하세요" />
            </div>
            <div class="filter-item">
              <label>상태</label>
              <a-select v-model:value="searchParams.status" style="width: 100%">
                <a-select-option value="">전체</a-select-option>
                <a-select-option value="ACTIVE">활성</a-select-option>
                <a-select-option value="EXPIRED">만료</a-select-option>
                <a-select-option value="PENDING">대기</a-select-option>
                <a-select-option value="CANCELED">취소</a-select-option>
                <a-select-option value="SUSPENDED">정지</a-select-option>
              </a-select>
            </div>
            <div class="filter-item">
              <label>계약 기간</label>
              <div class="date-range-picker">
                <a-range-picker 
                  v-model:value="dateRange"
                  format="YYYY-MM-DD"
                  :placeholder="['시작일', '종료일']"
                />
              </div>
            </div>
            <div class="filter-actions">
              <a-button type="primary" class="yellow-button" @click="searchSubscriptions">검색</a-button>
              <a-button @click="resetSearch">초기화</a-button>
            </div>
          </div>
        </div>
        
        <!-- 구독 목록 테이블 -->
        <div class="table-toolbar">
          <div class="total-count">
            전체 <span>{{ totalItems }}</span>건
          </div>
          <div class="table-actions">
            <a-button type="primary" class="yellow-button add-btn" @click="navigateToAddSubscription">
              구독 추가
            </a-button>
            <a-select 
              v-model:value="pageSize" 
              style="width: 140px"
              @change="handleDropdownPageSizeChange"
            >
              <a-select-option :value="10">10개씩 보기</a-select-option>
              <a-select-option :value="20">20개씩 보기</a-select-option>
              <a-select-option :value="50">50개씩 보기</a-select-option>
              <a-select-option :value="100">100개씩 보기</a-select-option>
            </a-select>
          </div>
        </div>
        
        <div class="table-responsive table-wrap">
          <a-table
            class="ant-table-striped"
            size="small"
            bordered
            :loading="loading"
            :scroll="tableScroll"
            :columns="columns"
            :data-source="subscriptionList"
            :pagination="false"
          >
            <template #emptyText>
              <div class="noData">
                <i class="fa-solid fa-exclamation-triangle" style="font-size: 24px; margin-bottom: 10px"/><br>
                <span>등록된 구독이 없습니다.</span>
              </div>
            </template>
            
            <!-- 셀 커스텀 렌더링 -->
            <template #bodyCell="{ column, text, record }">
              <!-- 상태 표시 -->
              <template v-if="column.dataIndex === 'status'">
                <a-tag :color="getStatusColor(text)">{{ getStatusText(text) }}</a-tag>
              </template>
              
              <!-- 금액 표시 -->
              <template v-if="column.dataIndex === 'price' || column.dataIndex === 'priceExcludingVAT' || column.dataIndex === 'priceIncludingVAT'">
                {{ text?.toLocaleString() || '0' }}원
              </template>
              
              <!-- 날짜 표시 -->
              <template v-if="column.dataIndex === 'contractStartDate'">
                <div class="date-with-button">
                  <div>{{ formatDate(text) }}</div>
                  <div class="button-cell-container">
                    <a-button 
                      size="small" 
                      class="day-calc-btn"
                      @click="handleDayCalculation(record)"
                    >
                      첫회차 일할 계산 보기
                    </a-button>
                  </div>
                </div>
              </template>
              <template v-if="column.dataIndex === 'contractEndDate'">
                {{ formatDate(text) }}
              </template>
              
              <!-- 연락처 표시 -->
              <template v-if="column.dataIndex === 'managerContact'">
                {{ formatMobileNumber(text) }}
              </template>
              
              <!-- 결제 예정일 표시 -->
              <template v-if="column.dataIndex === 'paymentDueDate'">
                매월 5 일
              </template>
              
              <!-- 결제카드 표시 -->
              <template v-if="column.dataIndex === 'paymentCard'">
                <div class="card-manage-container">
                  <span class="card-company">{{ getActiveCardCompany(record) }}</span>
                  <div class="button-cell-container">
                    <a-button 
                      size="small" 
                      class="card-manage-btn" 
                      @click="handleManageCard(record)"
                    >
                      카드관리
                    </a-button>
                  </div>
                </div>
              </template>
              
              <!-- 결제내역 표시 -->
              <template v-if="column.dataIndex === 'paymentHistory'">
                <div class="button-cell-container">
                  <a-button 
                    size="small" 
                    class="payment-history-btn" 
                    @click="handleViewPaymentHistory(record)"
                  >
                    보기
                  </a-button>
                </div>
              </template>
              
              <!-- 결제하기 표시 -->
              <template v-if="column.dataIndex === 'makePayment'">
                <div class="button-cell-container">
                  <a-button 
                    size="small" 
                    class="make-payment-btn"
                    @click="handleMakePayment(record)"
                  >
                    결제하기
                  </a-button>
                </div>
              </template>
              
              <!-- 작업 버튼 -->
              <template v-if="column.dataIndex === 'actions'">
                <a-space>
                  <a-button size="small" type="primary" @click="handleEditSubscription(record)">수정</a-button>
                  <a-button size="small" danger @click="handleDeleteSubscription(record)">삭제</a-button>
                </a-space>
              </template>
            </template>
          </a-table>
        </div>
        
        <!-- 페이지네이션 -->
        <div class="pagination-container">
          <a-pagination
            v-model:current="currentPage"
            :total="totalItems"
            :page-size="pageSize"
            :page-size-options="['10', '20', '50', '100']"
            show-size-changer
            show-quick-jumper
            @change="handlePageChange"
            @showSizeChange="handlePageSizeChange"
          />
        </div>
      </div>
    </div>
  </div>
  
  <!-- 결제내역 모달 -->
  <payment-history-modal
    v-model:visible="showPaymentHistoryModal"
    :subscription="selectedPaymentSubscription"
    @close="showPaymentHistoryModal = false"
  />

  <!-- 일할계산 모달 -->
  <daily-rate-modal
    v-model:visible="showDailyRateModal"
    :subscription="selectedSubscription"
    @close="showDailyRateModal = false"
  />
  
  <!-- 카드 관리 모달 -->
  <card-management-modal
    v-model:visible="showCardModal"
    :subscription="selectedCardSubscription"
    @card-updated="handleCardUpdated"
  />
</template>

<script>
import { ref, reactive, onMounted, getCurrentInstance, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import salesRecordsApis from '../api';
import DailyRateModal from './DailyRateModal.vue';
import CardManagementModal from './CardManagementModal.vue';
import PaymentHistoryModal from './PaymentHistoryModal.vue';
import { twoBtnDialog } from '@/utils/utils';

export default {
  name: 'CenterflowSubscriptionList',
  components: {
    DailyRateModal,
    CardManagementModal,
    PaymentHistoryModal
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalItems = ref(0);
    const dateRange = ref([]);
    const internalInstance = getCurrentInstance();
    const globalProperties = internalInstance?.appContext.config.globalProperties;

    // 테이블 스크롤 설정 (마운트 후 적용하여 ResizeObserver 오류 방지)
    const tableScroll = ref(null);
    
    // 검색 매개변수
    const searchParams = reactive({
      hospitalName: '',
      status: '',
      hospitalType: '',
      startDate: null,
      endDate: null
    });
    
    // 토스트 메시지 표시 함수
    const showToast = (type, message) => {
      if (globalProperties?.$message) {
        globalProperties.$message[type](message);
      } else {
        alert(message);
      }
    };
    
    // 테이블 열 정의
    const columns = [
      { title: '번호', dataIndex: 'index', key: 'index', align: 'center', width: 70 },
      { title: '병원명', dataIndex: 'hospitalName', key: 'hospitalName', align: 'center', width: 150 },
      { title: '상품명', dataIndex: 'productName', key: 'productName', align: 'center', width: 150 },
      { title: '설치비', dataIndex: 'price', key: 'price', align: 'right', width: 120 },
      { title: '판매가(VAT제외)', dataIndex: 'priceExcludingVAT', key: 'priceExcludingVAT', align: 'right', width: 150 },
      { title: '판매가(VAT포함)', dataIndex: 'priceIncludingVAT', key: 'priceIncludingVAT', align: 'right', width: 150 },
      { title: '계약 시작일', dataIndex: 'contractStartDate', key: 'contractStartDate', align: 'center', width: 120 },
      { title: '계약 종료일', dataIndex: 'contractEndDate', key: 'contractEndDate', align: 'center', width: 120 },
      { title: '결제 예정일', dataIndex: 'paymentDueDate', key: 'paymentDueDate', align: 'center', width: 120 },
      { title: '결제카드', dataIndex: 'paymentCard', key: 'paymentCard', align: 'center', width: 150 },
      { title: '결제내역', dataIndex: 'paymentHistory', key: 'paymentHistory', align: 'center', width: 120 },
      { title: '결제하기', dataIndex: 'makePayment', key: 'makePayment', align: 'center', width: 120 },
      { title: '담당 회사명', dataIndex: 'managerCompanyName', key: 'managerCompanyName', align: 'center', width: 150 },
      { title: '담당자', dataIndex: 'managerName', key: 'managerName', align: 'center', width: 100 },
      { title: '연락처', dataIndex: 'managerContact', key: 'managerContact', align: 'center', width: 130 },
      { title: '상태', dataIndex: 'status', key: 'status', align: 'center', width: 100 },
      { title: '작업', dataIndex: 'actions', key: 'actions', align: 'center', width: 120, fixed: 'right' }
    ];
    
    // 구독 목록
    const subscriptionList = ref([]);
    
    // API 인스턴스 가져오기
    const apiHandler = () => {
      return salesRecordsApis(globalProperties?.$apiInstance, globalProperties?.$store);
    };
    
    // 데이터 로드 함수
    const loadData = () => {
      loading.value = true;
      
      const params = {
        page: currentPage.value,
        limit: pageSize.value,
        ...searchParams
      };
      
      apiHandler().getCenterflowSubscriptions(
        params,
        (response) => {
          const { docs, totalDocs } = response.data;
          
          // 데이터 가공 및 인덱스 추가
          subscriptionList.value = docs.map((item, index) => ({
            ...item,
            key: item.id,
            index: (currentPage.value - 1) * pageSize.value + index + 1
          }));
          
          totalItems.value = totalDocs;
          loading.value = false;
        },
        (error) => {
          console.error('구독 목록 로드 에러:', error);
          loading.value = false;
          showToast('error', '구독 목록을 불러오는데 실패했습니다.');
        }
      );
    };
    
    // 검색 함수
    const searchSubscriptions = () => {
      currentPage.value = 1;
      
      if (dateRange.value && dateRange.value.length === 2) {
        searchParams.startDate = dateRange.value[0]?.format('YYYY-MM-DD') || null;
        searchParams.endDate = dateRange.value[1]?.format('YYYY-MM-DD') || null;
      } else {
        searchParams.startDate = null;
        searchParams.endDate = null;
      }
      
      loadData();
    };
    
    // 검색 초기화
    const resetSearch = () => {
      searchParams.hospitalName = '';
      searchParams.status = '';
      searchParams.hospitalType = '';
      dateRange.value = [];
      searchParams.startDate = null;
      searchParams.endDate = null;
      
      loadData();
    };
    
    // 페이지 변경 핸들러
    const handlePageChange = (page) => {
      currentPage.value = page;
      loadData();
    };
    
    // 페이지 크기 변경 핸들러 (페이지네이션 컴포넌트용)
    const handlePageSizeChange = (current, size) => {
      pageSize.value = size;
      currentPage.value = 1;
      loadData();
    };
    
    // 페이지 크기 변경 핸들러 (드롭다운용)
    const handleDropdownPageSizeChange = (size) => {
      pageSize.value = size;
      currentPage.value = 1;
      loadData();
    };
    
    // 구독 추가 페이지로 이동
    const navigateToAddSubscription = () => {
      router.push('/salesrecords/centerflow-subscription/create');
    };
    
    // 구독 편집 핸들러
    const handleEditSubscription = (record) => {
      router.push(`/salesrecords/centerflow-subscription/${record.id}/edit`);
    };
    
    // 구독 삭제 핸들러
    const handleDeleteSubscription = (record) => {
      // 확인 대화상자 표시
      if (window.confirm(`"${record.hospitalName}"의 구독을 삭제하시겠습니까?`)) {
        apiHandler().deleteCenterflowSubscription(
          record.id,
          (response) => {
            showToast('success', '구독이 성공적으로 삭제되었습니다.');
            loadData();
          },
          (error) => {
            showToast('error', '구독 삭제에 실패했습니다.');
          }
        );
      }
    };
    
    // 날짜 형식화 함수
    const formatDate = (timestamp) => {
      if (!timestamp) return '-';
      return dayjs(timestamp).format('YYYY-MM-DD');
    };
    
    // 상태에 따른 색상 가져오기
    const getStatusColor = (status) => {
      switch (status) {
        case 'ACTIVE': return 'green';
        case 'EXPIRED': return 'red';
        case 'PENDING': return 'orange';
        case 'CANCELED': return 'gray';
        case 'SUSPENDED': return 'purple';
        default: return 'default';
      }
    };
    
    // 상태 텍스트 가져오기
    const getStatusText = (status) => {
      switch (status) {
        case 'ACTIVE': return '활성';
        case 'EXPIRED': return '만료';
        case 'PENDING': return '대기';
        case 'CANCELED': return '취소';
        case 'SUSPENDED': return '정지';
        default: return '-';
      }
    };
    
    // 연락처 포맷팅 함수
    const formatMobileNumber = (value) => {
      if (!value) return '-';
      if (value.includes('-')) return value;
      
      const numbers = value.replace(/\D/g, '');
      if (!numbers) return '-';
      
      let formattedValue = numbers.slice(0, 3);
        
        if (numbers.length > 3) {
          const isFormat344 = numbers.startsWith('010');
          const secondGroupLength = isFormat344 ? 4 : 3;
          
          formattedValue += '-' + numbers.slice(3, 3 + secondGroupLength);
          
          if (numbers.length > 3 + secondGroupLength) {
            formattedValue += '-' + numbers.slice(3 + secondGroupLength, 3 + secondGroupLength + 4);
        }
      }
      
      return formattedValue || value;
    };
    
    // 활성화된 카드의 카드사 정보 가져오기
    const getActiveCardCompany = (record) => {
      if (!record.cardInfos || record.cardInfos.length === 0) {
        return '-';
      }
      
      const defaultActiveCard = record.cardInfos.find(card => card.isActive && card.isDefault);
      const activeCard = defaultActiveCard || record.cardInfos.find(card => card.isActive);
      
      return activeCard ? (activeCard.cardCompany || '-') : '-';
    };
    
    // 카드 관리 모달 관련 상태
    const showCardModal = ref(false);
    const selectedCardSubscription = ref({});
    
    // 카드 관리 버튼 클릭 핸들러
    const handleManageCard = (record) => {
      selectedCardSubscription.value = { ...record };
      showCardModal.value = true;
    };
    
    // 카드 업데이트 핸들러
    const handleCardUpdated = () => {
      loadData();
    };
    
    // 일할계산 모달 관련 상태
    const showDailyRateModal = ref(false);
    const selectedSubscription = ref({});
    
    // 일할계산 버튼 클릭 핸들러
    const handleDayCalculation = (record) => {
      selectedSubscription.value = { ...record };
      showDailyRateModal.value = true;
    };
    
    // 결제내역 모달 관련 상태
    const showPaymentHistoryModal = ref(false);
    const selectedPaymentSubscription = ref({});
    
    // 결제내역 보기 버튼 클릭 핸들러
    const handleViewPaymentHistory = (record) => {
      selectedPaymentSubscription.value = { ...record };
      showPaymentHistoryModal.value = true;
    };
    
    // 결제하기 버튼 클릭 핸들러
    const handleMakePayment = async (record) => {
      // 결제 예정 정보 가져오기
      const pendingPayments = (record.paymentHistory || [])
        .filter(p => p.paymentStatus === 'PENDING')
        .sort((a, b) => a.paymentRound - b.paymentRound);
      
      // 결제할 내역이 없을 경우
      if (pendingPayments.length === 0) {
        showToast('warning', '결제 예정된 내역이 없습니다.');
        return;
      }
      
      // 결제할 내역 (가장 오래된 PENDING 상태)
      const nextPayment = pendingPayments[0];
      
      // 결제 금액 포맷팅
      const formattedAmount = nextPayment.regularPaymentAmount.toLocaleString() + '원';
      
      // 확인 대화상자 표시
      const confirmResult = await twoBtnDialog(
        '결제 확인',
        `${record.hospitalName}의 ${nextPayment.paymentRound}회차 결제(${formattedAmount})를 진행하시겠습니까?`,
        '결제하기'
      );
      
      // 취소 시 종료
      if (!confirmResult) return;
      
      // API 인스턴스 가져오기
      const api = salesRecordsApis(globalProperties?.$apiInstance, globalProperties?.$store);
      
      try {
        // 결제 API 호출
        await api.processCenterflowPayment(
          record.id,
          {
            paymentRound: nextPayment.paymentRound,
            orderName: `${record.hospitalName} ${nextPayment.paymentRound}회차 정기결제`
          },
          (response) => {
            // API 호출은 성공했지만, 실제 결제 성공 여부 확인
            const paymentResult = response.data;
            
            if (paymentResult.paymentStatus === 'FAILED') {
              // 결제 실패 처리
              const failReason = paymentResult.failReason || '알 수 없는 오류로 결제가 실패했습니다.';
              showToast('error', `결제 실패: ${failReason}`);
            } else if (paymentResult.paymentStatus === 'COMPLETED') {
              // 결제 성공 처리
              showToast('success', '결제가 성공적으로 처리되었습니다.');
            } else {
              // 기타 상태 처리
              showToast('warning', `결제 상태: ${paymentResult.paymentStatus}`);
            }
            
            // 데이터 갱신
            loadData();
          },
          (error) => {
            // API 호출 자체 실패 처리
            const errorMessage = error.response?.data?.message || '결제 처리 중 오류가 발생했습니다.';
            showToast('error', errorMessage);
          }
        );
      } catch (error) {
        showToast('error', '결제 처리 중 예기치 않은 오류가 발생했습니다.');
      }
    };
    
    onMounted(() => {
      loadData();
      // 마운트 후 다음 틱에서 스크롤 설정 적용 (ResizeObserver 오류 방지)
      nextTick(() => {
        setTimeout(() => {
          tableScroll.value = { x: '1200px' };
        }, 100);
      });
    });
    
    return {
      loading,
      currentPage,
      pageSize,
      totalItems,
      searchParams,
      dateRange,
      columns,
      subscriptionList,
      searchSubscriptions,
      resetSearch,
      handlePageChange,
      handlePageSizeChange,
      handleDropdownPageSizeChange,
      navigateToAddSubscription,
      handleEditSubscription,
      handleDeleteSubscription,
      formatDate,
      getStatusColor,
      getStatusText,
      formatMobileNumber,
      handleManageCard,
      handleViewPaymentHistory,
      handleMakePayment,
      handleDayCalculation,
      showDailyRateModal,
      selectedSubscription,
      getActiveCardCompany,
      showCardModal,
      selectedCardSubscription,
      handleCardUpdated,
      showPaymentHistoryModal,
      selectedPaymentSubscription,
      tableScroll
    };
  }
};
</script>

<style scoped lang="scss">
.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}

.table-filter-container {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.filter-item {
  flex: 1;
  min-width: 200px;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
  }
}

.date-range-picker {
  width: 100%;
}

.filter-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 2px;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
}

.total-count {
  font-size: 14px;
  
  span {
    font-weight: bold;
    color: #1890ff;
  }
}

.table-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.add-btn {
  margin-right: 8px;
}

.table-wrap {
  margin: 0 16px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.noData {
  text-align: center;
  padding: 24px;
  color: #999;
}

.card-manage-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.card-company {
  font-weight: 500;
  display: block;
  margin-bottom: 4px;
}

.button-cell-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  height: auto;
}

.card-manage-btn,
.payment-history-btn,
.make-payment-btn,
.day-calc-btn {
  background-color: #f0f0f0;
  border-color: #d9d9d9;
  color: #333;
  margin: 0 !important;
  padding: 0 8px !important;
  height: 24px !important;
  line-height: 22px !important;
  font-size: 12px !important;
  
  &:hover {
    background-color: #e6e6e6;
    border-color: #c0c0c0;
    color: #000;
  }
}

.date-with-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

:deep(.ant-table-cell) {
  &[align="center"] {
    text-align: center !important;
    padding: 4px !important;
    vertical-align: middle !important;
  }
}

:deep(.ant-table-tbody > tr > td) {
  transition: none !important;
  padding: 4px 8px !important;
}

@media screen and (max-width: 767px) {
  .filter-row {
    flex-direction: column;
    gap: 12px;
  }
  
  .filter-item {
    width: 100%;
  }
  
  .table-toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .table-actions {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
